import { Cross2Icon } from '@radix-ui/react-icons'
import { formatUtcTimestamp } from '../../utils/dates'
import {
  booleanToOnOff,
  DeviceEvent,
  DeviceEventType,
  EventDetails,
  getEventName,
  parseEventDetails,
} from '../../types/DeviceEventTypes'
import { useEffect, useState } from 'react'

interface IPopoutProps {
  setShowPopout: (show: boolean) => void
  popoutData: DeviceEvent
}

const DeviceEventPopoutContent = (props: IPopoutProps) => {
  const { setShowPopout, popoutData } = props
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null)

  useEffect(() => {
    if (popoutData.eventDetails) {
      const parsedDetails = parseEventDetails(popoutData)
      setEventDetails(parsedDetails)
    } else {
      setEventDetails(null)
    }
  }, [popoutData.eventDetails])

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="font-semibold border-mainBorder border-b flex px-6 py-3 bg-mainBackground text-lg">
          Details
          <div className="flex justify-end items-center pr-2 flex-grow">
            <div
              className="cursor-pointer"
              onClick={() => {
                setShowPopout(false)
              }}
            >
              <Cross2Icon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 mb-8">
          <div className="leading-normal flex flex-col gap-2 px-8">
            <div className="font-semibold">Event Time</div>
            <div className="text-sm">{formatUtcTimestamp(popoutData.eventTime, false, true)}</div>
          </div>
          <div className="leading-normal flex flex-col gap-2 px-8">
            <div className="font-semibold">Event Type</div>
            <div className="text-sm">{getEventName(popoutData.eventType)}</div>
          </div>
          {popoutData.alert && (
            <div className="leading-normal flex flex-col gap-2 px-8">
              <div className="font-semibold">Alert Details</div>
              <div className="text-sm">
                <div className="mb-4">
                  <p>Name: {popoutData.alert.alertName}</p>
                  <p>Status: {popoutData.alert.alertStatus}</p>
                  <p>Severity: {popoutData.alert.severity}</p>
                </div>
              </div>
            </div>
          )}
          {eventDetails && (
            <>
              {eventDetails.fileSystemEventDetails && (
                <div className="leading-normal flex flex-col gap-2 px-8">
                  <div className="font-semibold">File Details</div>
                  <div className="text-sm">{eventDetails.fileSystemEventDetails}</div>
                </div>
              )}
              {eventDetails.processName && (
                <div className="leading-normal flex flex-col gap-2 px-8">
                  <div className="font-semibold">Crashlog Process Name</div>
                  <div className="text-sm">{eventDetails.processName}</div>
                </div>
              )}
              {eventDetails.exceptionType && (
                <div className="leading-normal flex flex-col gap-2 px-8">
                  <div className="font-semibold">Crashlog Exception Type</div>
                  <div className="text-sm">{eventDetails.exceptionType}</div>
                </div>
              )}
              {eventDetails.oldValue && (
                <div className="leading-normal flex flex-col gap-2 px-8">
                  <div className="font-semibold">Old Value</div>
                  <div className="text-sm">
                    {(() => {
                      switch (popoutData.eventType) {
                        case DeviceEventType.BIOMETRICS_CHANGED:
                        case DeviceEventType.SCREEN_LOCK_CHANGED:
                        case DeviceEventType.IS_NOTIFICATIONS_ENABLED_CHANGED:
                        case DeviceEventType.IS_WORK_PROFILE_CHANGED:
                        case DeviceEventType.IS_PLAY_PROTECT_ENABLED:
                        case DeviceEventType.IS_SIDELOADING_ENABLED:
                        case DeviceEventType.IS_DEVELOPER_MODE_ENABLED:
                        case DeviceEventType.IS_ADB_ENABLED:
                        case DeviceEventType.LOCKDOWN_ENABLED:
                        case DeviceEventType.SMISHING_ENABLED:
                          return booleanToOnOff(eventDetails.oldValue)
                        case DeviceEventType.COUNTRY_CODE:
                        case DeviceEventType.TELCO:
                          return eventDetails.oldValue == 'null' ? 'Unknown' : eventDetails.oldValue ?? 'N/A'
                        case DeviceEventType.TELCO_SET:
                        case DeviceEventType.COUNTRY_CODE_SET:
                          return eventDetails.oldValue == 'null' ? '--' : eventDetails.oldValue ?? '--'
                        default:
                          return eventDetails.oldValue ?? 'N/A'
                      }
                    })()}
                  </div>
                </div>
              )}
              {eventDetails.newValue && (
                <div className="leading-normal flex flex-col gap-2 px-8">
                  <div className="font-semibold">
                    {popoutData.eventType === DeviceEventType.DEVICE_OUT_OF_POLICY ? 'Out of Policy' : 'New Value'}
                  </div>
                  <div className="text-sm">
                    {(() => {
                      switch (popoutData.eventType) {
                        case DeviceEventType.BIOMETRICS_CHANGED:
                        case DeviceEventType.SCREEN_LOCK_CHANGED:
                        case DeviceEventType.IS_NOTIFICATIONS_ENABLED_CHANGED:
                        case DeviceEventType.IS_WORK_PROFILE_CHANGED:
                        case DeviceEventType.IS_PLAY_PROTECT_ENABLED:
                        case DeviceEventType.IS_SIDELOADING_ENABLED:
                        case DeviceEventType.IS_DEVELOPER_MODE_ENABLED:
                        case DeviceEventType.IS_ADB_ENABLED:
                        case DeviceEventType.LOCKDOWN_ENABLED:
                        case DeviceEventType.SMISHING_ENABLED:
                          return booleanToOnOff(eventDetails.newValue)
                        case DeviceEventType.DEVICE_OUT_OF_POLICY: {
                          const newValues: string[] = JSON.parse(eventDetails.newValue)
                          return (
                            <>
                              <ul>
                                {newValues.map((value, index) => (
                                  <li key={index}>{value}</li>
                                ))}
                              </ul>
                            </>
                          )
                        }
                        default:
                          return eventDetails.newValue ?? 'N/A'
                      }
                    })()}
                  </div>
                </div>
              )}
            </>
          )}

          {popoutData.fileId && (
            <div className="leading-normal flex flex-col gap-2 px-8">
              <div className="font-semibold">File ID</div>
              <div className="text-sm">{popoutData.fileId ?? 'N/A'}</div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
export default DeviceEventPopoutContent
